import type { FC } from 'react';
import type { EChartsOption } from 'echarts';
import type { RootState } from '@/store/store';
import type { IChartLabels } from '../../../types';
import { ChartsTypeEnum, type ILineConfigItem } from '../../../types';

import { init } from 'echarts';
import { useEffect, useRef, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { DashboardSearchContext } from '@/pages/dashboard-search';

type ILineChartProps = {
  height: number;
  data: ILineConfigItem[];
  yAxisInverse: boolean;
  showChart: boolean;
  labels: IChartLabels;
};

const LineChart: FC<ILineChartProps> = ({
  data,
  height,
  showChart,
  labels,
  yAxisInverse = false,
}) => {
  const { currentTheme } = useSelector((state: RootState) => state.theme);

  const { colorMap, onlyMyBrandsMap } = useContext(DashboardSearchContext);

  const sharesRef = useRef<HTMLDivElement | null>(null);

  const options: EChartsOption = useMemo(() => {
    const reversedData = [...data].reverse();
    const legend = [...new Set(reversedData.map(({ brand }) => brand))];
    const xAxisData = [...new Set(reversedData.map(({ date }) => date))];
    const options: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        // valueFormatter: (value) => String((+value).toFixed(2)),
      },
      dataset: [
        {
          dimensions: ['brand', 'yValue', 'date'],
          source: reversedData,
          id: 'dataset',
        },
        ...legend.map((brand) => ({
          fromId: 'dataset',
          id: brand,
          transform: {
            type: 'filter',
            config: { dimension: 'brand', value: brand },
          },
        })),
      ],
      series: legend.map((brand) => ({
        type: ChartsTypeEnum.line,
        name: brand,
        color: onlyMyBrandsMap?.[brand] ?? colorMap.get(brand),
        datasetId: brand,
        encode: {
          x: 'date',
          y: 'yValue',
        },
      })),
      legend: {
        data: legend,
        type: 'scroll',
        bottom: 0,
        textStyle: {
          color: currentTheme === 'dark' ? 'white' : 'black',
        },
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        name: labels?.x,
        nameLocation: 'middle',
        nameGap: 25,
        axisLine: {
          onZero: false,
        },
      },
      yAxis: {
        type: 'value',
        inverse: yAxisInverse,
        name: labels?.y,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 25,
      },
      dataZoom: [
        {
          type: 'inside',
        },
        {
          type: 'inside',
        },
      ],
    };

    return options;
  }, [data, currentTheme, yAxisInverse, colorMap]);

  useEffect(() => {
    const myChart = init(sharesRef.current);
    showChart && myChart.setOption(options);

    return () => myChart.dispose();
  }, [options, height, showChart]);

  return (
    <div
      className={`${height === 500 ? 'h-[500px]' : 'h-[300px]'} w-full`}
      ref={sharesRef}
    />
  );
};

export default LineChart;
